import { useMemo } from 'react';
import { Highlighter } from 'react-bootstrap-typeahead';

import { searchEvents } from 'api/events/endpoints/searchEvents';
import type { EventRow } from 'api/events/endpoints/getEvents';

import { EditReference } from './EditReference';
import type { ReferenceSource } from './referenceSource';

interface Props {
  // The stored `zone_name|name` event path, or null for "no event".
  readonly value: string | null;
  readonly onChange: (value: string | null) => void;
  /** Floating label. Omit to render a bare control (the caller supplies its own label), as `EditString` does. */
  readonly label?: string;
  /** Text shown while nothing is selected. Say what empty means, rather than restating the label. */
  readonly placeholder?: string;
  readonly mandatory?: boolean;
  readonly disabled?: boolean;
  // Restrict the searchable set to one zone (server-side `zone_name` equals). Undefined widens to
  // all authorized events.
  readonly zoneName?: string;
  // Which event kinds are selectable. `'all'` widens to every kind.
  readonly type?: 'processEvent' | 'fileEvent' | 'all';
}

const getKey = (e: EventRow) => `${e.zone_name}|${e.name}`;
const getLabel = (e: EventRow): string => `${e.name} (${e.zone_name})`;

const splitPath = (path: string): { readonly zone_name: string; readonly name: string } => {
  const [zone_name = '', name = ''] = path.split('|');
  return { zone_name, name };
};

// A key holds both display parts, so an item can be reconstructed from it alone. `event_id` is not
// part of the key and this control never reads it back, hence the placeholder 0.
const asItem = (path: string): EventRow => ({ event_id: 0, ...splitPath(path) });

/**
 * Searchable dropdown for events keyed on the `zone_name|name` path, for callers that store the
 * composite path. Selection returns the path.
 *
 * Sibling of the `event_id`-keyed {@link EditEventReference}, whose props are kept in step with these:
 * only `value` / `onChange` differ, in type *and* in meaning, which is the whole reason there are two
 * components rather than one with a `string | number` value.
 *
 * Backed by server-side search, so the option list is not bounded by what fits in one eager fetch.
 * Two-column row layout (name + zone, muted right-aligned as informational context); the server
 * matches on name only, zone is shown so users can disambiguate visually.
 */
export const EditEventPathReference = ({ value, onChange, label, placeholder, mandatory, disabled, zoneName, type = 'processEvent' }: Props) => {
  const selected = useMemo<EventRow | null>(() => (value === null || value === '' ? null : asItem(value)), [value]);

  // Re-created when the scope changes so the model re-fetches; stable otherwise (a fresh closure
  // would rebuild the model every render).
  const source = useMemo<ReferenceSource<EventRow>>(() => {
    const kind = type === 'all' ? undefined : type;
    return {
      kind: 'search',
      // `name` rather than `search`: the backend's `search` also matches on description, which would
      // show rows whose visible label does not contain what the user typed.
      search: (query, limit) => searchEvents({ name: query || undefined, limit, zone_name: zoneName, type: kind }),
      // Required by the source contract, which cannot know whether a consumer ever holds a key-only
      // value — but neither runs here: this key carries the whole display value, so the item the
      // control is handed is already labelled and there is nothing to hydrate. Built from the key
      // anyway, since that is both the cheapest correct answer and the only one available offline.
      resolve: async (key) => asItem(String(key)),
      interimValue: (key) => asItem(String(key)),
    };
  }, [zoneName, type]);

  return (
    <EditReference<EventRow>
      label={label}
      placeholder={placeholder}
      value={selected}
      onChange={(e) => onChange(e !== null ? getKey(e) : null)}
      source={source}
      getKey={getKey}
      getLabel={getLabel}
      mandatory={mandatory}
      disabled={disabled}
      renderMenuItemChildren={(e, query) => (
        <div className="d-flex justify-content-between gap-3">
          <span>
            <Highlighter search={query}>{e.name}</Highlighter>
          </span>
          <span className="text-body-secondary">{e.zone_name}</span>
        </div>
      )}
    />
  );
};
